import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ListAltOutlined from "@mui/icons-material/ListAltOutlined";
import { ResponsiveTable } from "@/components/Responsive";
import { useIsMobile } from "@/hooks/useIsMobile";
import { listCompactSx } from "@/styles/sx/compact";
import { type ReassistRequestSummary } from "../types";
import { formatDate } from "../utils/format";
import { responsiveTableWrapSx, tapExpandedSx, TEXT } from "../utils/tokens";
import { AmountText } from "./AmountText";
import { ProgressDots, ProgressStepper } from "./ProgressStepper";
import { RequestDetailBody } from "./RequestDetailBody";

interface Props {
  /** 表の aria-label（例「依頼一覧（対応中）」）。 */
  title: string;
  requests: ReassistRequestSummary[];
}

const HEADERS = ["依頼日", "納品日", "住所", "書類数", "進捗", "金額", "詳細"];
/** 金額の列（nth-of-type は 1 始まり）。列を増減しても右揃えがずれないよう、見出しから求める */
const AMOUNT_COLUMN = HEADERS.indexOf("金額") + 1;

/**
 * 依頼のテーブル形式。PC は表、SP は ResponsiveTable がカード（key-value）にする。
 * 書類の内訳・納品PDFは、行の内訳アイコン（aria-label「内訳を見る」）から開く詳細ダイアログで見せる
 * （ResponsiveTable に行の展開が無いため。SP は全画面）。
 */
export const RequestTable: React.FC<Props> = ({ title, requests }) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = requests.find((r) => r.requestId === selectedId) ?? null;

  const rows = requests.map((r) => [
    formatDate(r.requestedAt),
    r.deliveredAt !== null ? formatDate(r.deliveredAt) : "未納品",
    r.buildingName !== "" ? (
      <Box key="addr" component="span" sx={{ display: "block" }}>
        {r.address}
        <Typography
          component="span"
          sx={{ ...TEXT.meta, display: "block", color: "text.secondary" }}
        >
          {r.buildingName}
        </Typography>
      </Box>
    ) : (
      r.address
    ),
    `${r.documentCount ?? r.items.length}件`,
    <ProgressDots key="p" status={r.status} />,
    <AmountText
      key="a"
      amount={r.amount.total}
      taxIncluded
      isProvisional={r.amount.isProvisional}
    />,
    <Tooltip key="d" title="内訳を見る">
      <IconButton
        size="small"
        color="primary"
        aria-label={`${r.address}の内訳を見る`}
        className="TapExpanded"
        sx={tapExpandedSx}
        onClick={() => {
          setSelectedId(r.requestId);
        }}
      >
        <ListAltOutlined fontSize="small" />
      </IconButton>
    </Tooltip>,
  ]);

  return (
    <Box
      sx={{
        ...responsiveTableWrapSx(theme),
        // 金額は右揃え
        [`& .MuiTableCell-root:nth-of-type(${AMOUNT_COLUMN})`]: {
          textAlign: "right",
        },
      }}
    >
      <ResponsiveTable title={title} headers={HEADERS} rows={rows} />
      <Dialog
        fullScreen={isMobile}
        maxWidth={false}
        PaperProps={{ sx: { width: "100%", maxWidth: 640 } }}
        // ダイアログは画面ルートの外に描画されるため、SP の文字縮小をここで掛ける
        sx={isMobile ? listCompactSx : undefined}
        open={selected !== null}
        // 誤タップで閉じないよう、閉じるボタン以外（背景タップ・Esc）では閉じない
        onClose={(_, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setSelectedId(null);
        }}
      >
        {selected !== null && (
          <>
            <DialogTitle sx={{ overflowWrap: "anywhere" }}>
              {selected.address}
              {selected.buildingName !== "" && (
                <Typography
                  component="span"
                  sx={{
                    ...TEXT.body,
                    display: "block",
                    color: "text.primary",
                  }}
                >
                  {selected.buildingName}
                </Typography>
              )}
            </DialogTitle>
            <DialogContent dividers>
              <Typography
                variant="body2"
                sx={{ mb: 2, color: "text.secondary" }}
              >
                依頼日 {formatDate(selected.requestedAt)}
                {selected.deliveredAt !== null &&
                  `・納品日 ${formatDate(selected.deliveredAt)}`}
                ・書類 {selected.documentCount ?? selected.items.length}件
              </Typography>
              <Box sx={{ mb: 3 }}>
                <ProgressStepper status={selected.status} />
              </Box>
              <RequestDetailBody request={selected} />
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                className="TapExpanded"
                size="small"
                sx={tapExpandedSx}
                onClick={() => {
                  setSelectedId(null);
                }}
              >
                閉じる
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};
